import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, User, MapPin, Package, Scan, CheckCircle, ChevronRight, AlertTriangle, SkipForward, RefreshCw, List, Clock, CheckCircle2, Circle, Trash2, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const MobileFGPicking = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const slipNumber = searchParams.get('slip');
  const scanInputRef = useRef(null);
  // Submission lock ref — prevents a double-submit race when the scanner
  // fires Enter faster than React's setLoading(true) can update state, which
  // was causing the same USN to land in picked_packs twice.
  const submitLockRef = useRef(false);
  const [unpickingUsn, setUnpickingUsn] = useState(null);

  // States
  const [step, setStep] = useState('staff'); // staff, select-slip, progress-view, picking, success
  const [staffId, setStaffId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [pickSlips, setPickSlips] = useState([]);
  const [currentSlip, setCurrentSlip] = useState(null);
  const [currentItem, setCurrentItem] = useState(null);
  const [bins, setBins] = useState([]);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [scannedCode, setScannedCode] = useState('');
  const [progress, setProgress] = useState({ completed_items: 0, total_items: 0, percentage: 0 });
  const [loading, setLoading] = useState(false);
  
  // Progress view state (for session recovery)
  const [progressData, setProgressData] = useState(null);
  
  // Skip dialog
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [skipRemarks, setSkipRemarks] = useState('');

  // Force Submit (seasonal slips only) — finalize the whole slip as-is,
  // keeping picked items and skipping the rest. Mandatory remarks.
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceRemarks, setForceRemarks] = useState('');
  const [forceSubmitting, setForceSubmitting] = useState(false);
  
  // Skipped items for revoke
  const [skippedItems, setSkippedItems] = useState([]);
  const [showSkippedDialog, setShowSkippedDialog] = useState(false);
  const [revokeProcessing, setRevokeProcessing] = useState(null);

  // BATCH picking (2026-07): scans are accumulated client-side (instant, no
  // server call per scan) and submitted together — turning N slow round-trips
  // into one. Validation happens on submit; rejected packs are surfaced.
  const [pendingPacks, setPendingPacks] = useState([]);
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [lastRejected, setLastRejected] = useState([]);

  // Reset the client-side scan queue whenever the active item changes
  // (advance / skip / navigation) so queued packs can't be submitted
  // against the wrong item.
  useEffect(() => {
    setPendingPacks([]);
    setLastRejected([]);
  }, [currentItem?.index]);

  useEffect(() => {
    if (step === 'staff') {
      setTimeout(() => document.getElementById('fg-staff-input')?.focus(), 100);
    } else if (step === 'picking') {
      setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  }, [step]);

  // 2026-05-18 — Re-sticker workflow state for picker mobile UI.
  // `lastPickResticker` flashes a 🔴/🟢 chip after every scan;
  // `restickerSummary` powers the slip-level summary bar.
  const [lastPickResticker, setLastPickResticker] = useState(null);
  const [restickerSummary, setRestickerSummary] = useState({
    needs_resticker: 0, clean: 0, total_picked: 0,
  });
  // Stable WebAudio context for the two-tone beep (lazy-created on first
  // pick so iOS doesn't block it for missing user gesture).
  const audioCtxRef = useRef(null);
  const playBeep = (variant) => {
    try {
      if (!audioCtxRef.current) {
        const Ctor = window.AudioContext || window.webkitAudioContext;
        if (!Ctor) return;
        audioCtxRef.current = new Ctor();
      }
      const ctx = audioCtxRef.current;
      const now = ctx.currentTime;
      // Single mellow chirp (E5, sine, 80ms) on a successful pick.
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = 'sine';
      o.frequency.setValueAtTime(659.25, now);             // E5
      g.gain.setValueAtTime(0.12, now);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.09);
      o.start(now); o.stop(now + 0.10);
    } catch { /* audio unavailable — silent fallback */ }
  };

  const vibrate = (pattern = [50]) => {
    if (navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  };

  const handleStaffValidate = async () => {
    if (!staffId.trim()) {
      toast.error('Please scan Staff ID');
      vibrate([100, 50, 100]);
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.get(`/validate/staff/${staffId}`);
      if (response.data.valid) {
        setStaffName(response.data.staff_name);
        vibrate([50]);
        toast.success(`Welcome, ${response.data.staff_name}!`);
        
        if (slipNumber) {
          await startSlip(slipNumber);
        } else {
          // fetchPendingSlips will handle the step transition
          await fetchPendingSlips();
        }
      } else {
        toast.error(response.data.message || 'Invalid Staff ID');
        vibrate([100, 50, 100]);
      }
    } catch (error) {
      // Check for authentication error
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else {
        toast.error(error.response?.data?.detail || 'Staff validation failed');
      }
      vibrate([100, 50, 100]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingSlips = async () => {
    try {
      setLoading(true);
      // Use FIFO auto-assignment - only get the assigned slip for this picker
      const response = await apiClient.get(`/wms/fg/pick-slips/my-assigned?staff_id=${staffId}`);
      
      if (response.data.has_slip && response.data.slip) {
        const slip = response.data.slip;
        
        // Check if it's a Design Transfer slip - redirect to Design Transfer Picker
        if (slip.slip_type === 'FG_TRANSFER') {
          toast.info('Design Transfer slip assigned - redirecting...');
          navigate(`/wms/fg/design-transfer-picker?slip=${encodeURIComponent(slip.slip_number || slip.pick_slip_number)}`);
          return;
        }
        
        // Check if slip is IN_PROGRESS - show progress view first
        if (slip.status === 'IN_PROGRESS') {
          // Fetch progress data to show completed/pending USNs
          try {
            const progressResponse = await apiClient.get(`/wms/fg/pick-slip-progress?slip_number=${encodeURIComponent(slip.pick_slip_number)}`);
            setProgressData(progressResponse.data);
            setCurrentSlip(slip);
            setStep('progress-view');
            return;
          } catch (err) {
            console.error('Error fetching progress:', err);
            // Fall through to normal start
          }
        }
        
        // Regular FG slip - auto-start
        setPickSlips([slip]);
        await startSlip(slip.pick_slip_number);
      } else {
        setPickSlips([]);
        setStep('select-slip'); // Show "no slips available" state
        // Show the message from API (e.g., "not in today's pickers list")
        if (response.data.message) {
          toast.info(response.data.message);
        }
      }
    } catch (error) {
      console.error('Fetch pending slips error:', error);
      // Check for authentication error
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to get assigned pick slip');
      }
      setPickSlips([]);
      setStep('select-slip');
    } finally {
      setLoading(false);
    }
  };

  // Function to continue picking from progress view
  const continueFromProgress = async () => {
    if (currentSlip) {
      setProgressData(null);
      await startSlip(currentSlip.pick_slip_number);
    }
  };

  // Function to refresh progress data
  const refreshProgress = async () => {
    if (!currentSlip) return;
    setLoading(true);
    try {
      const progressResponse = await apiClient.get(`/wms/fg/pick-slip-progress?slip_number=${encodeURIComponent(currentSlip.pick_slip_number)}`);
      setProgressData(progressResponse.data);
    } catch (err) {
      toast.error('Failed to refresh progress');
    } finally {
      setLoading(false);
    }
  };

  const startSlip = async (slipNum) => {
    try {
      setLoading(true);
      // Show picking skeleton immediately for better UX
      setStep('picking');
      
      // Check slip status first - use query parameter endpoint
      const detailResponse = await apiClient.get(`/wms/fg/pick-slip-detail?slip_number=${encodeURIComponent(slipNum)}`);
      setCurrentSlip(detailResponse.data);
      
      // Extract skipped items from the slip (OLD format: is_partial: true with remarks)
      // These are items that were marked as completed but with partial quantity
      const skipped = (detailResponse.data.items || [])
        .map((item, index) => ({ ...item, item_index: index }))
        .filter(item => item.is_partial === true && item.remarks);
      setSkippedItems(skipped);
      
      // Start if imported or assigned - use query parameter endpoint
      if (detailResponse.data.status === 'IMPORTED' || detailResponse.data.status === 'ASSIGNED') {
        await apiClient.post(`/wms/fg/pick-slips/start-by-number?slip_number=${encodeURIComponent(slipNum)}&staff_id=${staffId}`);
      }
      
      // Get first item
      await fetchNextItem(slipNum);
    } catch (error) {
      console.error('Start slip error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start slip');
      // If start fails, go back to no-slip state
      setPickSlips([]);
      setCurrentItem(null);
      setStep('select-slip');
    } finally {
      setLoading(false);
    }
  };

  const fetchNextItem = async (slipNum) => {
    try {
      // Use query parameter endpoint to handle slashes in slip numbers
      const response = await apiClient.get(`/wms/fg/pick-slip-next-item?slip_number=${encodeURIComponent(slipNum)}`);
      
      if (response.data.has_next) {
        setCurrentItem(response.data.item);
        setBins(response.data.bins || []);
        setTotalAvailable(response.data.total_available);
        setProgress(response.data.progress);
        if (response.data.resticker_summary) {
          setRestickerSummary(response.data.resticker_summary);
        }
        setTimeout(() => scanInputRef.current?.focus(), 100);
      } else {
        setCurrentItem(null);
        setStep('success');
      }
    } catch (error) {
      toast.error('Failed to get next item');
    }
  };

  // Refresh bins for current item (useful when waiting for put-away)
  const refreshBins = async () => {
    if (!currentSlip) return;
    
    try {
      setLoading(true);
      const response = await apiClient.get(`/wms/fg/pick-slip-next-item?slip_number=${encodeURIComponent(currentSlip.pick_slip_number)}`);
      
      if (response.data.has_next) {
        const returnedItem = response.data.item;
        
        // Sync both item AND bins together to prevent desync
        // If backend returns a different item (due to route optimization), update it
        if (returnedItem.barcode !== currentItem?.barcode) {
          // Item changed - update everything
          setCurrentItem(returnedItem);
          toast.info(`Switched to: ${returnedItem.barcode}`);
        } else {
          // Same item - just update quantities in case they changed
          setCurrentItem(prev => ({
            ...prev,
            picked_qty: returnedItem.picked_qty,
            remaining_qty: returnedItem.remaining_qty
          }));
        }
        
        // Always update bins
        setBins(response.data.bins || []);
        setTotalAvailable(response.data.total_available);
        setProgress(response.data.progress);
        
        if (response.data.bins?.length > 0) {
          toast.success(`Found ${response.data.total_available} pcs available!`);
          vibrate([50, 50, 50]);
        } else {
          toast.info('No stock available yet. Try again after put-away.');
        }
      }
    } catch (error) {
      toast.error('Failed to refresh');
    } finally {
      setLoading(false);
    }
  };

  // Force refresh current item and bins - fixes desync issues
  const refreshCurrentItem = async () => {
    if (!currentSlip) return;
    
    try {
      setLoading(true);
      const response = await apiClient.get(`/wms/fg/pick-slip-next-item?slip_number=${encodeURIComponent(currentSlip.pick_slip_number)}`);
      
      if (response.data.has_next) {
        const returnedItem = response.data.item;
        
        // Always force update both item AND bins together
        setCurrentItem(returnedItem);
        setBins(response.data.bins || []);
        setTotalAvailable(response.data.total_available);
        setProgress(response.data.progress);
        
        vibrate([50]);
        toast.success('Refreshed!');
        
        // Focus scan input after refresh
        setTimeout(() => scanInputRef.current?.focus(), 100);
      } else {
        // Slip completed
        setCurrentItem(null);
        setStep('success');
      }
    } catch (error) {
      toast.error('Failed to refresh');
    } finally {
      setLoading(false);
    }
  };

  // Advance UI to the next item using guidance from a pick/batch response.
  const applyNextGuidance = async (d) => {
    if (d.slip_complete) {
      toast.success('All items picked! Slip completed.');
      setStep('success');
      return;
    }
    const nx = d.next;
    if (nx && nx.has_next) {
      setCurrentItem(nx.item);
      setBins(nx.bins || []);
      setTotalAvailable(nx.total_available);
      setProgress(nx.progress);
      if (nx.resticker_summary) setRestickerSummary(nx.resticker_summary);
    } else if (nx && !nx.has_next) {
      toast.success('All items picked!');
      setStep('success');
      return;
    } else {
      await fetchNextItem(currentSlip.pick_slip_number);
    }
    scanInputRef.current?.focus();
  };

  // BATCH: add a scanned pack to the client-side queue (instant, no server
  // call). Auto-submits once the queue reaches the item's remaining qty.
  const addPackToBatch = () => {
    const code = scannedCode.trim();
    if (!code) {
      toast.error('Please scan pack');
      vibrate([100, 50, 100]);
      return;
    }
    if (currentItem && currentItem.remaining_qty <= 0) {
      toast.error('Item already complete.');
      setScannedCode('');
      return;
    }
    if (pendingPacks.includes(code)) {
      toast.error(`${code} already in the list`);
      vibrate([100, 50, 100]);
      setScannedCode('');
      scanInputRef.current?.focus();
      return;
    }
    const next = [...pendingPacks, code];
    setPendingPacks(next);
    setLastRejected([]);
    setScannedCode('');
    playBeep('clean');
    vibrate([40]);
    scanInputRef.current?.focus();
    // Auto-submit the moment we've queued the full remaining quantity.
    if (currentItem && next.length >= currentItem.remaining_qty) {
      submitBatch(next);
    }
  };

  const removePendingPack = (code) => {
    setPendingPacks((prev) => prev.filter((c) => c !== code));
    scanInputRef.current?.focus();
  };

  // BATCH: submit all queued packs for the current item in ONE request.
  const submitBatch = async (packsArg) => {
    const packs = packsArg || pendingPacks;
    if (!packs.length) {
      toast.error('Scan at least one pack first');
      return;
    }
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    try {
      setBatchSubmitting(true);
      const response = await apiClient.post(
        `/wms/fg/pick-slip-pick-packs-batch?slip_number=${encodeURIComponent(currentSlip.pick_slip_number)}`,
        { item_index: currentItem.index, pack_barcodes: packs }
      );
      const d = response.data;
      setPendingPacks([]);

      if (d.accepted_count > 0) {
        playBeep('clean');
        vibrate([50]);
        toast.success(`✓ ${d.accepted_count} pack(s) picked`);
      }
      if (d.rejected_count > 0) {
        setLastRejected(d.rejected || []);
        vibrate([100, 50, 100]);
        toast.error(`⚠ ${d.rejected_count} rejected — see list below`, { duration: 5000 });
      } else {
        setLastRejected([]);
      }
      if (d.resticker_summary) setRestickerSummary(d.resticker_summary);
      await applyNextGuidance(d);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit packs');
      vibrate([100, 50, 100]);
      scanInputRef.current?.focus();
    } finally {
      setBatchSubmitting(false);
      submitLockRef.current = false;
    }
  };


  // Unpick / delete a completed USN from this slip. Used by the per-row
  // delete button in the Completed USNs panel to correct accidental
  // duplicate scans or wrong-pack scans before the slip is handed over.
  const handleUnpickUsn = async (packBarcode) => {
    if (!currentSlip?.pick_slip_number) return;
    if (!window.confirm(`Remove USN ${packBarcode} from picked list? The piece will go back to STORED so it can be re-picked.`)) {
      return;
    }
    try {
      setUnpickingUsn(packBarcode);
      await apiClient.post(
        `/wms/fg/pick-slip-unpick-usn?slip_number=${encodeURIComponent(currentSlip.pick_slip_number)}&pack_barcode=${encodeURIComponent(packBarcode)}`
      );
      toast.success(`USN ${packBarcode} removed`);
      // Refresh progress snapshot shown in the Completed USNs / Pending Items panels.
      try {
        const progressResponse = await apiClient.get(
          `/wms/fg/pick-slip-progress?slip_number=${encodeURIComponent(currentSlip.pick_slip_number)}`
        );
        setProgressData(progressResponse.data);
      } catch (_e) { /* non-fatal */ }
      // If the picker is actively picking, re-fetch the current item so the
      // remaining_qty reflects the unpick and the newly-reverted USN is scannable.
      if (step === 'picking') {
        await fetchNextItem(currentSlip.pick_slip_number);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unpick');
    } finally {
      setUnpickingUsn(null);
    }
  };

  const handleSkipItem = async () => {
    if (!skipRemarks.trim()) {
      toast.error('Remarks are required');
      return;
    }

    try {
      setLoading(true);
      // Use the OLD working endpoint - marks item as COMPLETED with is_partial: true
      const response = await apiClient.post(
        `/wms/fg/pick-slip-complete-item?slip_number=${encodeURIComponent(currentSlip.pick_slip_number)}&item_index=${currentItem.index}&remarks=${encodeURIComponent(skipRemarks)}`
      );
      
      toast.success('Item skipped');
      setSkipDialogOpen(false);
      setSkipRemarks('');
      
      // Backend returns "slip_complete" not "slip_completed"
      if (response.data.slip_complete) {
        setStep('success');
      } else {
        await fetchNextItem(currentSlip.pick_slip_number);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to skip');
    } finally {
      setLoading(false);
    }
  };

  // Force Submit (seasonal only): finalize the whole slip as-is — keep what's
  // picked, skip the rest. Mandatory remarks.
  const handleForceSubmit = async () => {
    if (!forceRemarks.trim()) {
      toast.error('Remarks are required for Force Submit');
      return;
    }
    try {
      setForceSubmitting(true);
      const response = await apiClient.post('/wms/fg/pick-slips/force-submit', {
        slip_number: currentSlip.pick_slip_number,
        remarks: forceRemarks.trim(),
      });
      toast.success(response.data?.message || 'Slip force-submitted');
      setForceDialogOpen(false);
      setForceRemarks('');
      setStep('success');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to force submit');
    } finally {
      setForceSubmitting(false);
    }
  };
  
  // Revoke skip - make skipped item (is_partial: true) available for picking again
  const handleRevokeSkip = async (itemIndex) => {
    setRevokeProcessing(itemIndex);
    try {
      await apiClient.post(
        `/wms/fg/pick-slips/${encodeURIComponent(currentSlip.pick_slip_number)}/revoke-skip`,
        {
          item_index: itemIndex,
          staff_id: staffId
        }
      );
      
      toast.success('Skip revoked - item is now available for picking');
      setShowSkippedDialog(false);
      
      // Refresh slip and items
      await startSlip(currentSlip.pick_slip_number);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke skip');
    } finally {
      setRevokeProcessing(null);
    }
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  const progressPercentage = progress.total_items > 0 
    ? Math.round((progress.completed_items / progress.total_items) * 100) 
    : 0;

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      {/* Header */}
      <header className="bg-gradient-to-r from-amber-600 to-amber-800 px-4 py-4 safe-area-inset-top">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/wms/mobile')}
            className="text-white hover:bg-white/20 -ml-2"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <div className="text-center">
            <h1 className="text-xl font-bold">FG Picking</h1>
            {currentSlip && (
              <p className="text-sm text-amber-200">{currentSlip.pick_slip_number}</p>
            )}
          </div>
          {/* Skipped items button - only show during picking */}
          {step === 'picking' && skippedItems.length > 0 ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowSkippedDialog(true)}
              className="text-white hover:bg-white/20 relative"
              data-testid="show-skipped-btn"
            >
              <SkipForward className="w-6 h-6" />
              <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {skippedItems.length}
              </span>
            </Button>
          ) : (
            <div className="w-10" />
          )}
        </div>
        
        {step === 'picking' && (
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-1">
              <span>{progress.completed_items} of {progress.total_items} items</span>
              <span>{progressPercentage}%</span>
            </div>
            <Progress value={progressPercentage} className="h-3 bg-amber-900" />
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1 p-4 flex flex-col">
        {/* Step: Staff Validation */}
        {step === 'staff' && (
          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-gray-800 rounded-3xl p-6 space-y-6">
              <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto">
                <User className="w-10 h-10 text-amber-400" />
              </div>
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-2">Scan Staff ID</h2>
                <p className="text-gray-400">Scan badge to start dispatch picking</p>
              </div>
              <Input
                id="fg-staff-input"
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, handleStaffValidate)}
                placeholder="Staff ID"
                className="h-16 text-xl text-center bg-gray-700 border-gray-600 rounded-xl"
                data-testid="mobile-fgpick-staff-input"
              />
              <Button
                onClick={handleStaffValidate}
                disabled={loading || !staffId}
                className="w-full h-16 text-xl font-bold bg-amber-600 hover:bg-amber-700 rounded-xl"
              >
                {loading ? 'Validating...' : 'Continue'}
              </Button>
            </div>
          </div>
        )}

        {/* Step: Select Slip (No slips available state) */}
        {step === 'select-slip' && (
          <div className="flex-1 flex flex-col">
            {pickSlips.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center">
                <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mb-4">
                  <Package className="w-10 h-10 text-gray-500" />
                </div>
                <p className="text-gray-400 text-lg">No pick slips assigned to you</p>
                <p className="text-sm text-gray-500 mt-2">Slips are auto-assigned based on FIFO</p>
                <Button
                  variant="outline"
                  onClick={() => navigate('/wms/mobile')}
                  className="mt-6 border-gray-600"
                >
                  Back to Dashboard
                </Button>
              </div>
            ) : (
              // This state shouldn't happen as slips are auto-started
              <div className="flex-1 flex flex-col items-center justify-center">
                <div className="animate-spin w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full mb-4" />
                <p className="text-gray-400">Loading your assigned slip...</p>
              </div>
            )}
          </div>
        )}

        {/* Step: Progress View - Session Recovery */}
        {step === 'progress-view' && progressData && (
          <div className="flex-1 flex flex-col space-y-4">
            {/* Progress Summary Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm text-blue-200">Slip #{progressData.slip_number}</p>
                  <p className="text-lg font-bold">{progressData.customer}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={refreshProgress}
                  disabled={loading}
                  className="border-white/30 text-white hover:bg-white/10"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <div className="mb-2">
                <div className="flex justify-between text-sm mb-1">
                  <span>{progressData.progress_summary.total_picked} of {progressData.progress_summary.total_required} picked</span>
                  <span>{progressData.progress_summary.percentage}%</span>
                </div>
                <Progress value={progressData.progress_summary.percentage} className="h-3 bg-blue-900" />
              </div>
              <div className="flex gap-4 text-sm">
                <Badge className="bg-green-500/20 text-green-300">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {progressData.progress_summary.usns_scanned} USNs scanned
                </Badge>
                <Badge className="bg-orange-500/20 text-orange-300">
                  <Circle className="w-3 h-3 mr-1" />
                  {progressData.progress_summary.total_remaining} remaining
                </Badge>
              </div>
            </div>

            {/* Completed USNs */}
            <div className="bg-gray-800 rounded-2xl p-4 flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                <h3 className="font-bold text-green-400">Completed USNs ({progressData.completed_usns.length})</h3>
              </div>
              <div className="flex-1 overflow-y-auto space-y-2 max-h-48">
                {progressData.completed_usns.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">No USNs picked yet</p>
                ) : (
                  progressData.completed_usns.map((usn, idx) => (
                    <div key={`${usn.usn_code}-${idx}`} className="bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2" data-testid={`completed-usn-row-${usn.usn_code}`}>
                      <div className="flex justify-between items-center gap-2">
                        <p className="font-mono text-sm text-green-300 flex-1 truncate">{usn.usn_code}</p>
                        <Badge variant="outline" className="text-xs border-green-500/50 text-green-400 shrink-0">
                          {usn.bin_code}
                        </Badge>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleUnpickUsn(usn.usn_code)}
                          disabled={unpickingUsn === usn.usn_code}
                          className="h-7 w-7 text-red-400 hover:text-red-300 hover:bg-red-500/20 shrink-0"
                          data-testid={`unpick-btn-${usn.usn_code}`}
                          title="Remove this USN from the picked list"
                        >
                          {unpickingUsn === usn.usn_code ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {usn.design_name} | {usn.shade} | {usn.size}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Pending Items */}
            <div className="bg-gray-800 rounded-2xl p-4 flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-5 h-5 text-orange-400" />
                <h3 className="font-bold text-orange-400">Pending Items ({progressData.pending_items.length})</h3>
              </div>
              <div className="flex-1 overflow-y-auto space-y-2 max-h-48">
                {progressData.pending_items.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">All items picked!</p>
                ) : (
                  progressData.pending_items.map((item, idx) => (
                    <div key={idx} className="bg-orange-500/10 border border-orange-500/30 rounded-lg px-3 py-2">
                      <div className="flex justify-between items-center">
                        <p className="font-medium text-orange-200">{item.design_name}</p>
                        <Badge variant="outline" className="text-xs border-orange-500/50 text-orange-400">
                          {item.remaining_qty} remaining
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {item.shade} | {item.size} | {item.picked_qty}/{item.required_qty} picked
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Continue Button */}
            <Button
              onClick={continueFromProgress}
              disabled={loading}
              className="w-full h-14 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-lg font-bold rounded-2xl"
            >
              {loading ? (
                <RefreshCw className="w-6 h-6 animate-spin" />
              ) : (
                <>
                  <Scan className="w-6 h-6 mr-2" />
                  Continue Picking
                </>
              )}
            </Button>
          </div>
        )}

        {/* Step: Picking - Loading State */}
        {step === 'picking' && !currentItem && (
          <div className="flex-1 flex flex-col space-y-3">
            {/* Loading Skeleton */}
            <div className="bg-gray-800 rounded-2xl p-4 animate-pulse">
              <div className="h-4 bg-gray-700 rounded w-24 mb-2"></div>
              <div className="h-8 bg-gray-700 rounded w-48 mb-4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-700 rounded w-full"></div>
                <div className="h-4 bg-gray-700 rounded w-3/4"></div>
              </div>
            </div>
            
            {/* Bin Guidance Skeleton */}
            <div className="bg-gray-800 rounded-2xl p-4 flex-1 animate-pulse">
              <div className="h-5 bg-gray-700 rounded w-32 mb-3"></div>
              <div className="space-y-2">
                <div className="h-16 bg-gray-700 rounded"></div>
                <div className="h-16 bg-gray-700 rounded"></div>
              </div>
            </div>
            
            {/* Scan Input Skeleton */}
            <div className="bg-gray-800 rounded-2xl p-4 animate-pulse">
              <div className="h-12 bg-gray-700 rounded mb-3"></div>
              <div className="h-14 bg-gray-700 rounded"></div>
            </div>
            
            {/* Loading indicator */}
            <div className="flex items-center justify-center py-4">
              <RefreshCw className="w-8 h-8 text-amber-400 animate-spin" />
              <span className="ml-3 text-amber-400 font-medium">Loading pick slip...</span>
            </div>
          </div>
        )}

        {/* Step: Picking */}
        {step === 'picking' && currentItem && (
          <div className="flex-1 flex flex-col space-y-3">
            {/* Sales Executive Remarks - Prominent Alert */}
            {currentItem.remarks && (
              <div className="bg-amber-500/20 border border-amber-500/50 rounded-2xl p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-amber-300">REMARKS FROM SALES:</p>
                    <p className="text-amber-100 font-medium">{currentItem.remarks}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Current Item */}
            <div className="bg-gray-800 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex-1">
                  <p className="text-sm text-gray-400">Pick Item</p>
                  <p className="font-mono font-bold text-xl text-amber-400">{currentItem.barcode}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={refreshCurrentItem}
                    disabled={loading}
                    className="border-blue-500/50 text-blue-400"
                    data-testid="mobile-fgpick-refresh-item"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSkipDialogOpen(true)}
                    className="border-orange-500/50 text-orange-400"
                  >
                    <SkipForward className="w-4 h-4 mr-1" />
                    Skip
                  </Button>
                </div>
              </div>
              
              {/* Item Details - Design, Shade, Size, Description */}
              <div className="mb-3 space-y-1">
                {currentItem.design_name && (
                  <p className="text-sm">
                    <span className="text-gray-500">Design:</span> 
                    <span className="text-white ml-1 font-medium">{currentItem.design_name}</span>
                  </p>
                )}
                <div className="flex gap-4 text-sm">
                  {currentItem.shade && (
                    <span>
                      <span className="text-gray-500">Shade:</span> 
                      <span className="text-white ml-1">{currentItem.shade}</span>
                    </span>
                  )}
                  {currentItem.size && (
                    <span>
                      <span className="text-gray-500">Size:</span> 
                      <span className="text-white ml-1">{currentItem.size}</span>
                    </span>
                  )}
                </div>
                {currentItem.description && (
                  <p className="text-xs text-gray-400 mt-1">{currentItem.description}</p>
                )}
              </div>
              
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-gray-700 rounded-xl p-2">
                  <p className="text-xs text-gray-400">Need</p>
                  <p className="font-bold text-lg">{currentItem.required_qty}</p>
                </div>
                <div className="bg-green-500/20 rounded-xl p-2">
                  <p className="text-xs text-green-400">Picked</p>
                  <p className="font-bold text-lg text-green-400">{currentItem.picked_qty}</p>
                </div>
                <div className="bg-blue-500/20 rounded-xl p-2">
                  <p className="text-xs text-blue-400">Left</p>
                  <p className="font-bold text-lg text-blue-400">{currentItem.remaining_qty}</p>
                </div>
              </div>
            </div>

            {/* FIFO Bin Guidance */}
            {bins.length > 0 ? (
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-blue-200">Go to (FIFO)</p>
                    <p className="text-2xl font-bold">{bins[0].bin_code}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold">{bins[0].available_qty}</p>
                    <p className="text-xs text-blue-200">available</p>
                  </div>
                </div>
                {bins.length > 1 && (
                  <div className="text-sm text-blue-200 border-t border-blue-500/30 pt-2 mt-2">
                    Next: {bins.slice(1, 3).map(b => `${b.bin_code} (${b.available_qty})`).join(', ')}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <AlertTriangle className="w-8 h-8 text-red-400" />
                  <div className="flex-1">
                    <p className="font-bold text-red-400">No Stock Available</p>
                    <p className="text-sm text-red-300">Wait for put-away or skip with remarks</p>
                  </div>
                </div>
                <Button
                  onClick={refreshBins}
                  disabled={loading}
                  variant="outline"
                  className="w-full h-12 border-red-500/50 text-red-300 hover:bg-red-500/20"
                  data-testid="mobile-fgpick-refresh-bins"
                >
                  <RefreshCw className={`w-5 h-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  {loading ? 'Checking...' : 'Check for New Stock'}
                </Button>
              </div>
            )}

            {/* Scan Input */}
            <div className="bg-gray-800 rounded-2xl p-4 space-y-3 flex-1">
              <div className="text-center">
                <h3 className="font-bold">Scan Pack</h3>
                <p className="text-sm text-gray-400">No USN restriction</p>
              </div>
              {/* 2026-05-18 — Last-pick re-sticker chip (flashes after every scan) */}
              {lastPickResticker && (
                <div
                  className={
                    "rounded-xl px-4 py-3 border text-center font-bold " +
                    (lastPickResticker.needs_resticker
                      ? "bg-rose-600/25 border-rose-500 text-rose-100"
                      : "bg-emerald-600/25 border-emerald-500 text-emerald-100")
                  }
                  data-testid={
                    lastPickResticker.needs_resticker
                      ? "mobile-fgpick-last-resticker-chip"
                      : "mobile-fgpick-last-clean-chip"
                  }
                >
                  {lastPickResticker.needs_resticker ? (
                    <>
                      <div className="text-2xl leading-tight">🔴 RESTICKER → PP BAG</div>
                      <div className="text-xs font-mono mt-1 opacity-90">
                        {lastPickResticker.pack_barcode}
                      </div>
                      <div className="text-sm mt-1">
                        MRP {lastPickResticker.from_mrp ?? '—'} → {lastPickResticker.to_mrp ?? '—'}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-2xl leading-tight">🟢 CLEAN → PACK BAG</div>
                      <div className="text-xs font-mono mt-1 opacity-90">
                        {lastPickResticker.pack_barcode}
                      </div>
                    </>
                  )}
                </div>
              )}
              <Input
                ref={scanInputRef}
                value={scannedCode}
                onChange={(e) => setScannedCode(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, addPackToBatch)}
                placeholder="Scan pack — adds to queue"
                className="h-14 text-lg text-center bg-gray-700 border-gray-600 rounded-xl font-mono"
                data-testid="mobile-fgpick-scan-input"
              />

              {/* Batch queue: scanned packs waiting to be submitted */}
              <div className="flex items-center justify-between px-1" data-testid="mobile-fgpick-queue-count">
                <span className="text-sm text-gray-300">
                  Queued: <span className="font-bold text-green-400">{pendingPacks.length}</span>
                  {currentItem?.remaining_qty ? ` / ${currentItem.remaining_qty}` : ''}
                </span>
                {pendingPacks.length > 0 && (
                  <button
                    onClick={() => setPendingPacks([])}
                    className="text-xs text-red-400 underline"
                    data-testid="mobile-fgpick-clear-queue"
                  >
                    Clear all
                  </button>
                )}
              </div>
              {pendingPacks.length > 0 && (
                <div className="max-h-32 overflow-y-auto space-y-1 bg-gray-900/60 rounded-xl p-2" data-testid="mobile-fgpick-queue-list">
                  {pendingPacks.map((code) => (
                    <div key={code} className="flex items-center justify-between bg-gray-700/70 rounded-lg px-3 py-1.5">
                      <span className="font-mono text-sm text-gray-100">{code}</span>
                      <button
                        onClick={() => removePendingPack(code)}
                        className="text-red-400 text-lg leading-none px-2"
                        data-testid={`mobile-fgpick-remove-${code}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <Button
                onClick={() => submitBatch()}
                disabled={batchSubmitting || pendingPacks.length === 0 || bins.length === 0}
                className="w-full h-14 text-xl font-bold bg-green-600 hover:bg-green-700 rounded-xl"
                data-testid="mobile-fgpick-submit-batch"
              >
                {batchSubmitting ? 'Submitting...' : `Submit ${pendingPacks.length || ''} Pack${pendingPacks.length === 1 ? '' : 's'}`}
              </Button>

              {/* Rejected packs from the last submit */}
              {lastRejected.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-3 space-y-1" data-testid="mobile-fgpick-rejected">
                  <p className="text-sm font-semibold text-red-400">
                    {lastRejected.length} pack(s) not picked — re-check:
                  </p>
                  {lastRejected.map((r, i) => (
                    <p key={i} className="text-xs text-red-300 font-mono">
                      {r.pack}: {r.reason}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* Low Stock Warning */}
            {totalAvailable > 0 && totalAvailable < currentItem.remaining_qty && (
              <div className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                <p className="text-sm text-yellow-400">
                  Only {totalAvailable} available. May need to skip with remarks.
                </p>
              </div>
            )}

            {/* Force Submit — seasonal pick slips only. Finalizes the whole
                slip as-is: keeps picked items, skips the rest. */}
            {currentSlip?.is_seasonal && (
              <div className="mt-auto pt-2">
                <Button
                  onClick={() => { setForceRemarks(''); setForceDialogOpen(true); }}
                  disabled={loading || forceSubmitting}
                  className="w-full h-12 text-base font-bold bg-rose-600 hover:bg-rose-700 rounded-xl"
                  data-testid="mobile-fgpick-force-submit-btn"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  Force Submit (Seasonal)
                </Button>
                <p className="text-[11px] text-gray-500 text-center mt-1">
                  Keeps picked pieces, skips the rest, closes the slip.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-gray-800 rounded-3xl p-8 text-center space-y-6">
              <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-14 h-14 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-green-400">Slip Complete!</h2>
                <p className="text-gray-400 mt-2">Ready for dispatch</p>
              </div>
              <div className="space-y-3">
                <Button
                  onClick={async () => {
                    setCurrentSlip(null);
                    setCurrentItem(null);
                    setProgress({ completed_items: 0, total_items: 0, percentage: 0 });
                    // Get next FIFO assigned slip
                    await fetchPendingSlips();
                  }}
                  className="w-full h-14 text-lg bg-amber-600 hover:bg-amber-700 rounded-xl"
                >
                  Get Next Slip
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/wms/mobile')}
                  className="w-full h-14 border-gray-600 text-gray-300 rounded-xl"
                >
                  Back to Dashboard
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Skip Dialog */}
      {/* Skip Dialog */}
      <Dialog open={skipDialogOpen} onOpenChange={setSkipDialogOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white mx-4 rounded-2xl">
          <DialogHeader>
            <DialogTitle>Skip Item / Partial Pick</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {currentItem && (
              <div className="p-3 bg-gray-700 rounded-xl text-sm">
                <p><strong>SKU:</strong> {currentItem.barcode}</p>
                <p><strong>Required:</strong> {currentItem.required_qty}</p>
                <p><strong>Picked:</strong> {currentItem.picked_qty}</p>
              </div>
            )}
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Remarks (Required)</label>
              <Textarea
                value={skipRemarks}
                onChange={(e) => setSkipRemarks(e.target.value)}
                placeholder="e.g., 'Out of stock', '5 pcs short'"
                className="bg-gray-700 border-gray-600 rounded-xl min-h-[100px]"
                data-testid="mobile-skip-remarks"
              />
            </div>
          </div>
          <DialogFooter className="flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setSkipDialogOpen(false)}
              className="flex-1 border-gray-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSkipItem}
              disabled={!skipRemarks.trim() || loading}
              className="flex-1 bg-orange-600 hover:bg-orange-700"
            >
              Skip Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force Submit Dialog (seasonal only) */}
      <Dialog open={forceDialogOpen} onOpenChange={setForceDialogOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white mx-4 rounded-2xl" data-testid="mobile-fgpick-force-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-400">
              <Zap className="w-5 h-5" />
              Force Submit Seasonal Slip
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-xl text-sm text-rose-200">
              This will <strong>close the slip now</strong>: everything already picked is kept,
              all remaining un-picked items are skipped. This can’t be undone.
            </div>
            {currentSlip && (
              <div className="p-3 bg-gray-700 rounded-xl text-sm">
                <p><strong>Slip:</strong> {currentSlip.pick_slip_number}</p>
                <p><strong>Progress:</strong> {progress.completed_items} of {progress.total_items} items</p>
              </div>
            )}
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Remarks (Required)</label>
              <Textarea
                value={forceRemarks}
                onChange={(e) => setForceRemarks(e.target.value)}
                placeholder="e.g., 'Balance stock not available', 'End of day close-out'"
                className="bg-gray-700 border-gray-600 rounded-xl min-h-[100px]"
                data-testid="mobile-fgpick-force-remarks"
              />
            </div>
          </div>
          <DialogFooter className="flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setForceDialogOpen(false)}
              disabled={forceSubmitting}
              className="flex-1 border-gray-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handleForceSubmit}
              disabled={!forceRemarks.trim() || forceSubmitting}
              className="flex-1 bg-rose-600 hover:bg-rose-700"
              data-testid="mobile-fgpick-force-confirm-btn"
            >
              {forceSubmitting ? 'Submitting…' : 'Force Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Skipped Items Dialog - Revoke Skip */}
      <Dialog open={showSkippedDialog} onOpenChange={setShowSkippedDialog}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white mx-4 rounded-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-400">
              <SkipForward className="w-5 h-5" />
              Skipped Items ({skippedItems.length})
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-2">
            {skippedItems.length === 0 ? (
              <p className="text-center text-gray-400 py-4">No skipped items</p>
            ) : (
              <div className="space-y-3">
                {skippedItems.map((item, idx) => (
                  <div key={idx} className="bg-gray-700 rounded-xl p-3 border border-orange-500/30">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-orange-400">
                          Item #{item.item_index + 1}
                        </p>
                        <p className="font-mono text-sm text-white">{item.barcode}</p>
                        <p className="text-xs text-gray-400">{item.design_name || item.design}</p>
                        <p className="text-xs text-gray-400">Required: {item.required_qty}</p>
                        <div className="mt-2 p-2 bg-gray-800 rounded-lg">
                          <p className="text-xs text-gray-500">Reason:</p>
                          <p className="text-sm text-gray-300">{item.remarks || item.skip_remarks || 'No reason'}</p>
                        </div>
                        {(item.skipped_by || item.completed_by) && (
                          <p className="text-xs text-gray-500 mt-1">
                            By: {item.skipped_by || item.completed_by || 'Unknown'}
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleRevokeSkip(item.item_index)}
                        disabled={revokeProcessing === item.item_index}
                        className="bg-green-600 hover:bg-green-700 shrink-0"
                        data-testid={`revoke-skip-${item.item_index}`}
                      >
                        {revokeProcessing === item.item_index ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Pick
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSkippedDialog(false)}
              className="w-full border-gray-600"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileFGPicking;
